import { useMemo } from 'react'

import { usePagination } from 'hooks/helpers'

import { Transaction } from 'state/user/types'
import { useTransactionsHistory } from 'state/user/hooks'

import { sliceHash } from 'utils/stringUtils'
import { getEtherscanLink } from 'utils/crypto'
import { TxStatuses, TxStatusesText, TxTypes } from 'constants/transaction'

import { Badge, Button, Icon } from 'components/ui'

import { createModal, ModalName, CloseButton } from 'modal'

import {
  Row,
  Wrap,
  Column,
  NoContent,
  IconWrap,
  Title,
  StyledLink,
  MobileHash,
  Pagination,
  DesktopHash,
  PaginationText,
  PaginationButtons,
} from './Style'

type Props = {
  onCurrentClose: VoidFunction
}

type TxProps = {
  array: Transaction[]
}

const humanizeStatus = {
  [TxTypes.REFUND]: 'REFUND',
  [TxTypes.REVOKE]: 'REVOKE',
  [TxTypes.APPROVE]: 'APPROVE',
  [TxTypes.AIRDROP]: 'AIRDROP',
  [TxTypes.TOP_UP_SELF_GENERATED]: 'TOP UP',
  [TxTypes.AIRDROP_WITH_SIGNATURE]: 'AIRDROP SIGNATURE',
}

export function Transactions({ array }: TxProps) {
  const arrayWithoutFailedTxs = array.filter((tx) => tx.status && tx.status !== TxStatuses.FAIL)

  const pagination = usePagination(
    arrayWithoutFailedTxs.sort((a, b) => {
      if (!b.timestamp) {
        return 1
      }
      if (!a.timestamp) {
        return -1
      }
      return Number(b.timestamp) - Number(a.timestamp)
    }),
  )

  return (
    <>
      <Row type='header'>
        <Column type='typeValue'>Type</Column>
        <Column>TxHash</Column>
        <Column type='status'> Status</Column>
      </Row>

      {pagination.data.map(({ txHash, type, status, chainId }: Transaction) => (
        <Row key={txHash}>
          <Column type='typeValue'>{humanizeStatus[type]}</Column>
          <Column>
            <StyledLink href={`${getEtherscanLink(chainId, txHash, 'transaction')}`}>
              <MobileHash>{humanizeStatus[type]}</MobileHash>
              <DesktopHash>{sliceHash(txHash)}</DesktopHash>
              <Icon name='externalLink' size='extraMedium' />
            </StyledLink>
          </Column>
          <Column type='status'>
            <Badge value={TxStatusesText[status]} />
          </Column>
        </Row>
      ))}

      <Pagination>
        <PaginationText>
          Page: {pagination.page} of {pagination.lastPage}
        </PaginationText>

        <PaginationButtons>
          <Button type='primary' size='small' onClick={pagination.onFirstPage}>
            &lt;==
          </Button>
          <Button type='default' size='small' onClick={pagination.onPrevPage}>
            &lt;
          </Button>
          <Button type='default' size='small' onClick={pagination.onNextPage}>
            &gt;
          </Button>
          <Button type='primary' size='small' onClick={pagination.onLastPage}>
            ==&gt;
          </Button>
        </PaginationButtons>
      </Pagination>
    </>
  )
}

export function TxHistoryModal({ onCurrentClose }: Props) {
  const transactions = useTransactionsHistory()

  const transactionsArray: Transaction[] = Object.values(transactions)

  const isTransactionsArray = useMemo(() => transactionsArray.length !== 0, [transactionsArray])

  return (
    <Wrap>
      {isTransactionsArray ? (
        <Transactions array={transactionsArray} />
      ) : (
        <NoContent>
          <IconWrap>
            <Icon name='noContent' size='fill' />
          </IconWrap>
          <Title>You don`t have transactions yet</Title>
        </NoContent>
      )}

      <CloseButton onClick={onCurrentClose}>
        <Icon name='close' size='large' />
      </CloseButton>
    </Wrap>
  )
}

const newTxHistoryModal = () =>
  createModal({
    title: '_transaction-history',
    align: 'center',
    name: ModalName.TX_HISTORY,
    disableOverlayClose: true,
    // @ts-ignore
    container: <TxHistoryModal />,
  })

export { newTxHistoryModal }
